import { useState, useEffect } from 'react'
import { Palette, Mail, Globe, Save, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTheme } from '../ThemeProvider'

interface PreferencesSectionProps {
  onSave?: () => void
}

interface UserPreferences {
  emailNotifications: {
    marketingEmails: boolean
    productUpdates: boolean
    securityAlerts: boolean
  }
  language: string
}

export default function PreferencesSection({ onSave }: PreferencesSectionProps) {
  // Use shared theme context
  const { themePreference, setTheme } = useTheme()

  // Email notification preferences
  const [emailNotifications, setEmailNotifications] = useState({
    marketingEmails: false,
    productUpdates: true,
    securityAlerts: true,
  })

  // Language preference
  const [language, setLanguage] = useState<string>('en')

  // Load preferences from localStorage on mount
  useEffect(() => {
    try {
      const storedPrefs = localStorage.getItem('user-preferences')
      if (storedPrefs) {
        const parsedPrefs: UserPreferences = JSON.parse(storedPrefs)
        setEmailNotifications(parsedPrefs.emailNotifications || {
          marketingEmails: false,
          productUpdates: true,
          securityAlerts: true,
        })
        setLanguage(parsedPrefs.language || 'en')
      }
    } catch (error) {
      console.error('Failed to load preferences:', error)
    }
  }, [])

  // Save all preferences to localStorage
  const savePreferences = () => {
    const preferences: UserPreferences = {
      emailNotifications,
      language,
    }

    try {
      localStorage.setItem('user-preferences', JSON.stringify(preferences))

      // Show success toast
      showSuccessToast('Preferences saved successfully!')

      // Call parent callback if provided
      if (onSave) {
        onSave()
      }
    } catch (error) {
      console.error('Failed to save preferences:', error)
      showErrorToast('Failed to save preferences. Please try again.')
    }
  }

  // Handle theme change
  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme)
  }

  // Handle email notification toggle
  const handleEmailNotificationToggle = (key: keyof typeof emailNotifications) => {
    setEmailNotifications(prev => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  // Handle language change
  const handleLanguageChange = (newLanguage: string) => {
    setLanguage(newLanguage)
  }

  // Success toast state
  const [showSuccess, setShowSuccess] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  // Error toast state
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const showSuccessToast = (message: string) => {
    setSuccessMessage(message)
    setShowSuccess(true)
    setTimeout(() => setShowSuccess(false), 3000)
  }

  const showErrorToast = (message: string) => {
    setErrorMessage(message)
    setShowError(true)
    setTimeout(() => setShowError(false), 3000)
  }

  return (
    <>
      <div className="space-y-6">
        {/* Theme Preferences */}
        <div className="border border-border/50 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Palette className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">Theme</h3>
          </div>

          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Choose your preferred color theme for the application.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => handleThemeChange('light')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                  themePreference === 'light'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-border/80'
                }`}
              >
                <span className="text-xl">☀️</span>
                <span className="font-medium">Light</span>
              </button>

              <button
                onClick={() => handleThemeChange('dark')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                  themePreference === 'dark'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-border/80'
                }`}
              >
                <span className="text-xl">🌙</span>
                <span className="font-medium">Dark</span>
              </button>

              <button
                onClick={() => handleThemeChange('system')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                  themePreference === 'system'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-border/80'
                }`}
              >
                <span className="text-xl">💻</span>
                <span className="font-medium">System</span>
              </button>
            </div>

            <p className="text-xs text-muted-foreground">
              {themePreference === 'system'
                ? 'Theme will match your system preferences'
                : themePreference === 'dark'
                ? 'Dark theme is selected'
                : 'Light theme is selected'}
            </p>
          </div>
        </div>

        {/* Email Notification Preferences */}
        <div className="border border-border/50 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">Email Notifications</h3>
          </div>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Choose which email notifications you want to receive.
            </p>

            <div className="space-y-3">
              {/* Marketing Emails */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label htmlFor="marketing-emails" className="font-medium cursor-pointer">
                    Marketing Emails
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Receive promotional content and special offers
                  </p>
                </div>
                <button
                  id="marketing-emails"
                  onClick={() => handleEmailNotificationToggle('marketingEmails')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    emailNotifications.marketingEmails ? 'bg-primary' : 'bg-input'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      emailNotifications.marketingEmails ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Product Updates */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label htmlFor="product-updates" className="font-medium cursor-pointer">
                    Product Updates
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Receive news about features and improvements
                  </p>
                </div>
                <button
                  id="product-updates"
                  onClick={() => handleEmailNotificationToggle('productUpdates')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    emailNotifications.productUpdates ? 'bg-primary' : 'bg-input'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      emailNotifications.productUpdates ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Security Alerts */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label htmlFor="security-alerts" className="font-medium cursor-pointer">
                    Security Alerts
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Receive important security notifications
                  </p>
                </div>
                <button
                  id="security-alerts"
                  onClick={() => handleEmailNotificationToggle('securityAlerts')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    emailNotifications.securityAlerts ? 'bg-primary' : 'bg-input'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      emailNotifications.securityAlerts ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Language Preferences */}
        <div className="border border-border/50 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">Language</h3>
          </div>

          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Select your preferred language for the application interface.
            </p>

            <select
              value={language}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="en">English</option>
              <option value="de">Deutsch (German)</option>
              <option value="fr">Français (French)</option>
              <option value="es">Español (Spanish)</option>
              <option value="it">Italiano (Italian)</option>
              <option value="pt">Português (Portuguese)</option>
              <option value="zh">中文 (Chinese)</option>
              <option value="ja">日本語 (Japanese)</option>
            </select>

            <p className="text-xs text-muted-foreground">
              Note: Language translations are coming soon. English is currently the only supported language.
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4">
          <Button
            onClick={savePreferences}
            className="flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            Save Preferences
          </Button>
        </div>
      </div>

      {/* Success Toast */}
      {showSuccess && (
        <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-right">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-900 dark:text-green-100 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3">
            <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {showError && (
        <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-right">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-900 dark:text-red-100 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3">
            <span className="font-medium">{errorMessage}</span>
          </div>
        </div>
      )}
    </>
  )
}
